<template>
  <v-card class="diag-card rounded-lg" :class="{ 'mt-4': !embedded }" flat outlined>
    <v-card-title class="diag-card__title py-3">
      <v-icon left color="primary">mdi-monitor-dashboard</v-icon>
      Diagnóstico del sistema
      <v-spacer></v-spacer>
      <v-btn small outlined color="primary" :loading="midiendo" @click="refreshAll(true)">
        <v-icon left small>mdi-refresh</v-icon>
        Actualizar
      </v-btn>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pt-4">
      <!-- Panel usuario / dispositivo / registro (oculto; datos se guardan en BD) -->
      <div v-show="showPanelSesion" class="diag-section mb-4">
        <div class="diag-section__head">
          <v-icon small class="mr-1" color="primary">mdi-account-badge-outline</v-icon>
          Sesión y dispositivo
        </div>
        <div class="diag-grid">
          <div class="diag-item">
            <span class="diag-item__label">Usuario</span>
            <span class="diag-item__value">{{ panelUsuario.nombre || '—' }}</span>
          </div>
          <div class="diag-item">
            <span class="diag-item__label">ID usuario (ERP)</span>
            <span class="diag-item__value">{{ panelUsuario.iderp || '—' }}</span>
          </div>
          <div class="diag-item">
            <span class="diag-item__label">ID dispositivo</span>
            <span class="diag-item__value diag-item__value--mono">{{ deviceInfo.id || 'Obteniendo...' }}</span>
          </div>
          <div class="diag-item">
            <span class="diag-item__label">Plataforma</span>
            <span class="diag-item__value">{{ deviceInfo.platformLabel || '—' }}</span>
          </div>
          <div class="diag-item">
            <span class="diag-item__label">Último registro</span>
            <span class="diag-item__value">{{ registroInfo.ultimoGuardado || '—' }}</span>
          </div>
          <div class="diag-item">
            <span class="diag-item__label">Sincronización</span>
            <span class="diag-item__value" :class="registroInfo.error ? 'error--text' : 'success--text'">
              {{ registroInfo.estadoTexto }}
            </span>
          </div>
        </div>
      </div>

      <!-- Semáforo general -->
      <div class="diag-semaphore mb-4" :class="'diag-semaphore--' + overallStatus">
        <div class="diag-semaphore__panel">
          <div class="diag-semaphore__lights">
            <div
              class="diag-light diag-light--red"
              :class="{ 'diag-light--active': overallStatus === 'red' }"
              title="Problemas detectados"
            ></div>
            <div
              class="diag-light diag-light--yellow"
              :class="{ 'diag-light--active': overallStatus === 'yellow' }"
              title="Rendimiento aceptable"
            ></div>
            <div
              class="diag-light diag-light--green"
              :class="{ 'diag-light--active': overallStatus === 'green' }"
              title="Todo corre al 100%"
            ></div>
          </div>
          <div class="diag-semaphore__info">
            <div class="diag-semaphore__title">{{ overallStatusTitle }}</div>
            <div class="diag-semaphore__subtitle">{{ overallStatusText }}</div>
          </div>
        </div>
        <div class="diag-semaphore__checks mt-3">
          <div class="diag-check" :class="'diag-check--' + internetStatus">
            <span class="diag-check__dot"></span>
            Internet: {{ internetStatusLabel }}
          </div>
          <div class="diag-check" :class="'diag-check--' + latencyStatus">
            <span class="diag-check__dot"></span>
            Latencia: {{ latencyStatusLabel }}
          </div>
          <div class="diag-check" :class="'diag-check--' + hardwareStatus">
            <span class="diag-check__dot"></span>
            Equipo: {{ hardwareStatusLabel }}
          </div>
        </div>
      </div>

      <!-- Estado general -->
      <div class="d-flex flex-wrap align-center mb-4">
        <v-chip
          small
          class="mr-2 mb-2"
          :color="isOnline ? 'success' : 'error'"
          text-color="white"
        >
          <v-icon left x-small>{{ isOnline ? 'mdi-wifi' : 'mdi-wifi-off' }}</v-icon>
          {{ isOnline ? 'Conectado' : 'Sin internet' }}
        </v-chip>
        <v-chip
          v-if="isInternetSlow"
          small
          color="warning"
          text-color="white"
          class="mr-2 mb-2"
        >
          <v-icon left x-small>mdi-speedometer-slow</v-icon>
          Internet lento
        </v-chip>
        <v-chip
          v-if="isHardwareLimited"
          small
          color="orange darken-2"
          text-color="white"
          class="mr-2 mb-2"
        >
          <v-icon left x-small>mdi-memory</v-icon>
          Hardware limitado
        </v-chip>
        <v-chip
          v-if="!isInternetSlow && !isHardwareLimited && isOnline"
          small
          color="success"
          outlined
          class="mb-2"
        >
          <v-icon left x-small>mdi-check-circle-outline</v-icon>
          Conexión y equipo dentro de lo normal
        </v-chip>
      </div>

      <v-alert
        v-if="isInternetSlow"
        type="warning"
        dense
        outlined
        class="mb-4"
      >
        Tu conexión a internet es lenta o inestable. El sistema podría experimentar retrasos al cargar datos.
      </v-alert>

      <v-alert
        v-if="isHardwareLimited"
        type="info"
        dense
        outlined
        class="mb-4"
      >
        Tu equipo tiene recursos limitados (RAM, CPU o memoria del navegador). Ciertas vistas pueden sentirse lentas aunque el internet sea bueno.
      </v-alert>

      <!-- Referencia óptima vs actual -->
      <div class="diag-section mb-4">
        <div class="diag-section__head">
          <v-icon small class="mr-1" color="green darken-1">mdi-check-decagram</v-icon>
          Referencia óptima del sistema
        </div>
        <div class="diag-ref-table">
          <div class="diag-ref-row diag-ref-row--head">
            <span>Métrica</span>
            <span>Óptimo</span>
            <span>Tu equipo</span>
            <span>Estado</span>
          </div>
          <div
            v-for="item in referenciaComparativa"
            :key="item.key"
            class="diag-ref-row"
          >
            <span class="diag-ref-metric">{{ item.label }}</span>
            <span class="diag-ref-opt">{{ item.optimo }}</span>
            <span class="diag-ref-val" :class="'diag-ref-val--' + item.status">{{ item.actual }}</span>
            <span class="diag-ref-status">
              <v-icon x-small :color="item.status === 'green' ? 'success' : (item.status === 'yellow' ? 'warning' : 'error')">
                {{ item.status === 'green' ? 'mdi-check-circle' : (item.status === 'yellow' ? 'mdi-alert-circle' : 'mdi-close-circle') }}
              </v-icon>
            </span>
          </div>
        </div>
      </div>

      <v-row dense>
        <!-- Red -->
        <v-col cols="12" md="6">
          <div class="diag-section">
            <div class="diag-section__head">
              <v-icon small class="mr-1" color="teal">mdi-wifi-strength-4</v-icon>
              Conexión a internet
            </div>
            <div class="diag-grid">
              <div class="diag-item">
                <span class="diag-item__label">Estado</span>
                <span class="diag-item__value">{{ isOnline ? 'En línea' : 'Desconectado' }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Tipo efectivo</span>
                <span class="diag-item__value">{{ networkInfo.type || 'No disponible' }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Velocidad medida</span>
                <span class="diag-item__value" :class="speedStatusClass">
                  {{ formatSpeedTest(speedTestMbps) }}
                </span>
              </div>
              <div v-show="showMetodoMedicion" class="diag-item diag-item--full" v-if="speedTestDetail">
                <span class="diag-item__label">Método de medición</span>
                <span class="diag-item__value diag-item__value--muted">{{ speedTestDetail }}</span>
              </div>
              <div v-show="showReferenciaNavegador" class="diag-item">
                <span class="diag-item__label">Referencia navegador</span>
                <span class="diag-item__value diag-item__value--muted">{{ formatBrowserDownlink(networkInfo.downlink) }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Latencia (RTT)</span>
                <span class="diag-item__value" :class="latencyStatusClass">{{ formatRtt(networkInfo.rtt) }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Latencia real (ping)</span>
                <span class="diag-item__value" :class="latencyStatusClass">{{ pingMs != null ? pingMs + ' ms' : 'Midiendo...' }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Ahorro de datos</span>
                <span class="diag-item__value">{{ networkInfo.saveData ? 'Activado' : 'Desactivado' }}</span>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Hardware -->
        <v-col cols="12" md="6">
          <div class="diag-section">
            <div class="diag-section__head">
              <v-icon small class="mr-1" color="indigo">mdi-chip</v-icon>
              Hardware del equipo
            </div>
            <div class="diag-grid">
              <div class="diag-item">
                <span class="diag-item__label">Núcleos CPU</span>
                <span class="diag-item__value">{{ hardwareInfo.cores || 'No disponible' }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Memoria RAM (estimada)</span>
                <span class="diag-item__value">{{ formatDeviceMemory(hardwareInfo.deviceMemory) }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Almacenamiento navegador</span>
                <span class="diag-item__value">{{ storageInfo.availableText }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Uso almacenamiento</span>
                <span class="diag-item__value" :class="storageInfo.usageHigh ? 'warning--text font-weight-bold' : ''">
                  {{ storageInfo.usageText }}
                </span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Plataforma</span>
                <span class="diag-item__value">{{ hardwareInfo.platform || 'No disponible' }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Puntos táctiles</span>
                <span class="diag-item__value">{{ hardwareInfo.maxTouchPoints != null ? hardwareInfo.maxTouchPoints : 'N/A' }}</span>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Pantalla (oculta en UI, datos se siguen recolectando) -->
        <v-col v-show="showPantalla" cols="12" md="6">
          <div class="diag-section">
            <div class="diag-section__head">
              <v-icon small class="mr-1" color="blue">mdi-monitor</v-icon>
              Pantalla y ventana
            </div>
            <div class="diag-grid">
              <div class="diag-item">
                <span class="diag-item__label">Resolución pantalla</span>
                <span class="diag-item__value">{{ screenInfo.width }} × {{ screenInfo.height }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Ventana del navegador</span>
                <span class="diag-item__value">{{ screenInfo.innerWidth }} × {{ screenInfo.innerHeight }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Profundidad de color</span>
                <span class="diag-item__value">{{ screenInfo.colorDepth }} bits</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Densidad de píxeles</span>
                <span class="diag-item__value">{{ screenInfo.pixelRatio }}x</span>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Memoria JS / Navegador -->
        <v-col cols="12" md="6">
          <div class="diag-section">
            <div class="diag-section__head">
              <v-icon small class="mr-1" color="deep-purple">mdi-web</v-icon>
              Navegador y memoria
            </div>
            <div class="diag-grid">
              <div class="diag-item">
                <span class="diag-item__label">Navegador</span>
                <span class="diag-item__value">{{ browserInfo.name }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Idioma</span>
                <span class="diag-item__value">{{ browserInfo.language }}</span>
              </div>
              <div v-show="showZonaHoraria" class="diag-item">
                <span class="diag-item__label">Zona horaria</span>
                <span class="diag-item__value">{{ browserInfo.timezone }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Memoria JS usada</span>
                <span class="diag-item__value">{{ memoryInfo.used }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Límite memoria JS</span>
                <span class="diag-item__value">{{ memoryInfo.limit }}</span>
              </div>
              <div class="diag-item">
                <span class="diag-item__label">Uso memoria JS</span>
                <span class="diag-item__value" :class="memoryInfo.usageHigh ? 'warning--text font-weight-bold' : ''">
                  {{ memoryInfo.usagePercent }}
                </span>
              </div>
            </div>
          </div>
        </v-col>

        <!-- Ubicación -->
        <v-col cols="12">
          <div class="diag-section">
            <div class="diag-section__head d-flex align-center flex-wrap">
              <span>
                <v-icon small class="mr-1" color="red darken-1">mdi-map-marker-radius</v-icon>
                Ubicación
              </span>
              <v-spacer></v-spacer>
              <v-chip
                v-if="locationInfo.source === 'gps'"
                x-small
                color="success"
                text-color="white"
                class="ml-2"
              >
                GPS del navegador
              </v-chip>
              <v-chip
                v-else-if="locationInfo.source === 'ip'"
                x-small
                color="blue-grey"
                text-color="white"
                class="ml-2"
              >
                Aproximada por IP
              </v-chip>
            </div>

            <div v-if="locationInfo.loading" class="text-center py-6 grey--text">
              <v-progress-circular indeterminate color="primary" size="28" width="2"></v-progress-circular>
              <div class="text-caption mt-2">Obteniendo ubicación...</div>
            </div>

            <v-alert
              v-else-if="locationInfo.error"
              type="warning"
              dense
              outlined
              class="mb-0"
            >
              {{ locationInfo.error }}
            </v-alert>

            <template v-else-if="hasLocation">
              <div class="diag-grid mb-3">
                <div class="diag-item diag-item--full">
                  <span class="diag-item__label">Dirección</span>
                  <span class="diag-item__value">{{ locationInfo.address || 'No disponible' }}</span>
                </div>
                <div class="diag-item">
                  <span class="diag-item__label">Coordenadas</span>
                  <span class="diag-item__value">{{ formatCoords(locationInfo.lat, locationInfo.lng) }}</span>
                </div>
                <div class="diag-item">
                  <span class="diag-item__label">Precisión</span>
                  <span class="diag-item__value">{{ formatAccuracy(locationInfo.accuracy) }}</span>
                </div>
                <div class="diag-item" v-if="locationInfo.city">
                  <span class="diag-item__label">Ciudad</span>
                  <span class="diag-item__value">{{ locationInfo.city }}</span>
                </div>
                <div class="diag-item" v-if="locationInfo.region">
                  <span class="diag-item__label">Estado / región</span>
                  <span class="diag-item__value">{{ locationInfo.region }}</span>
                </div>
                <div class="diag-item" v-if="locationInfo.country">
                  <span class="diag-item__label">País</span>
                  <span class="diag-item__value">{{ locationInfo.country }}</span>
                </div>
              </div>

              <div v-show="showMapa" class="diag-map-wrap">
                <iframe
                  v-if="mapEmbedUrl"
                  class="diag-map"
                  :src="mapEmbedUrl"
                  title="Mapa de ubicación"
                  loading="lazy"
                ></iframe>
              </div>

              <div v-show="showMapa" class="d-flex flex-wrap align-center mt-2">
                <a
                  :href="externalMapUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-caption primary--text"
                >
                  <v-icon x-small color="primary">mdi-open-in-new</v-icon>
                  Abrir en OpenStreetMap
                </a>
                <span class="text-caption grey--text ml-3">
                  © OpenStreetMap contributors
                </span>
              </div>

              <div v-if="locationInfo.permissionDenied" class="text-caption grey--text mt-2">
                El navegador no compartió la ubicación exacta; se muestra una ubicación aproximada por IP.
              </div>
            </template>
          </div>
        </v-col>
      </v-row>

      <v-divider v-show="showDetalleTecnico" class="my-4"></v-divider>

      <div v-show="showDetalleTecnico" class="diag-section">
        <div class="diag-section__head mb-2">
          <v-icon small class="mr-1" color="grey darken-1">mdi-information-outline</v-icon>
          Detalle técnico
        </div>
        <div class="diag-useragent">{{ browserInfo.userAgent }}</div>
        <div class="text-caption grey--text mt-2">
          Última actualización: {{ lastUpdate || '—' }}
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'

var DIAG_LOCAL_KEY = 'diag_sistema_registros'
var DIAG_LAST_SEND_KEY = 'diag_sistema_ultimo_envio'
var DIAG_DEVICE_ID_KEY = 'diag_device_id'
var DIAG_SEND_INTERVAL_MS = 60 * 60 * 1000

var REFERENCIA_OPTIMA = {
  speedMbps: 20,
  pingMs: 120,
  cores: 4,
  ramGb: 4,
  memoryJsPercent: 80,
  storageAvailableMb: 500,
}

export default {
  name: 'DiagnosticoSistema',

  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      showMetodoMedicion: false,
      showReferenciaNavegador: false,
      showPantalla: false,
      showZonaHoraria: false,
      showMapa: false,
      showDetalleTecnico: false,
      showPanelSesion: false,
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
      networkInfo: {
        downlink: null,
        rtt: null,
        type: null,
        saveData: false,
      },
      hardwareInfo: {
        cores: null,
        deviceMemory: null,
        platform: null,
        maxTouchPoints: null,
      },
      screenInfo: {
        width: 0,
        height: 0,
        innerWidth: 0,
        innerHeight: 0,
        colorDepth: 0,
        pixelRatio: 1,
      },
      browserInfo: {
        name: '',
        userAgent: '',
        language: '',
        timezone: '',
      },
      memoryInfo: {
        used: 'No disponible',
        limit: 'No disponible',
        usagePercent: 'No disponible',
        usageHigh: false,
      },
      storageInfo: {
        quota: null,
        usage: null,
        available: null,
        availableText: 'No disponible',
        usageText: 'No disponible',
        usageHigh: false,
      },
      deviceInfo: {
        id: '',
        platform: '',
        platformLabel: '',
        model: '',
        osVersion: '',
        manufacturer: '',
        isVirtual: false,
      },
      registroInfo: {
        ultimoGuardado: '',
        ultimoEnvioServidor: '',
        enviando: false,
        error: '',
        estadoTexto: 'Pendiente',
      },
      hourlyTimer: null,
      pingMs: null,
      speedTestMbps: null,
      speedTestLoading: false,
      speedTestError: false,
      speedTestDetail: '',
      speedTestProgress: '',
      midiendo: false,
      lastUpdate: '',
      connectionRef: null,
      locationInfo: {
        lat: null,
        lng: null,
        accuracy: null,
        address: '',
        city: '',
        region: '',
        country: '',
        source: '',
        loading: false,
        error: '',
        permissionDenied: false,
      },
    }
  },

  computed: {
    ...mapGetters('login', ['getdatosUsuarioERPERP']),

    panelUsuario () {
      var u = this.getdatosUsuarioERPERP || {}
      var nombre = [u.nombre, u.apellido_paterno, u.apellido_materno].filter(Boolean).join(' ').trim()
      return {
        iderp: u.iderp || null,
        idUsuario: u.id_usuario || null,
        nombre: nombre || u.usuario || u.email || '',
        email: u.email || '',
      }
    },

    referenciaComparativa () {
      var memPct = this.memoryInfo.usagePercent !== 'No disponible'
        ? parseInt(this.memoryInfo.usagePercent, 10)
        : null
      var storageMb = this.storageInfo.available != null
        ? Math.round(this.storageInfo.available / (1024 * 1024))
        : null

      return [
        this.buildRefItem('speed', 'Velocidad internet', '≥ ' + REFERENCIA_OPTIMA.speedMbps + ' Mbps', this.formatSpeedTest(this.speedTestMbps), this.scoreSpeed()),
        this.buildRefItem('ping', 'Latencia (ping)', '≤ ' + REFERENCIA_OPTIMA.pingMs + ' ms', this.pingMs != null ? this.pingMs + ' ms' : 'Midiendo...', this.scorePing()),
        this.buildRefItem('cores', 'Núcleos CPU', '≥ ' + REFERENCIA_OPTIMA.cores, this.hardwareInfo.cores || 'N/D', this.scoreCores()),
        this.buildRefItem('ram', 'Memoria RAM', '≥ ' + REFERENCIA_OPTIMA.ramGb + ' GB', this.formatDeviceMemory(this.hardwareInfo.deviceMemory), this.scoreRam()),
        this.buildRefItem('jsmem', 'Memoria JS', '< ' + REFERENCIA_OPTIMA.memoryJsPercent + '%', this.memoryInfo.usagePercent, this.scoreJsMemory(memPct)),
        this.buildRefItem('storage', 'Espacio navegador libre', '≥ ' + REFERENCIA_OPTIMA.storageAvailableMb + ' MB', storageMb != null ? storageMb + ' MB' : 'N/D', this.scoreStorage(storageMb)),
        this.buildRefItem('online', 'Conexión', 'En línea', this.isOnline ? 'En línea' : 'Desconectado', this.isOnline ? 'green' : 'red'),
      ]
    },

    hasLocation () {
      return this.locationInfo.lat != null && this.locationInfo.lng != null
    },

    mapEmbedUrl () {
      if (!this.hasLocation) return ''
      var lat = this.locationInfo.lat
      var lng = this.locationInfo.lng
      var delta = this.locationInfo.source === 'ip' ? 0.08 : 0.012
      var bbox = (lng - delta) + '%2C' + (lat - delta) + '%2C' + (lng + delta) + '%2C' + (lat + delta)
      return 'https://www.openstreetmap.org/export/embed.html?bbox=' + bbox + '&layer=mapnik&marker=' + lat + '%2C' + lng
    },

    externalMapUrl () {
      if (!this.hasLocation) return '#'
      var lat = this.locationInfo.lat
      var lng = this.locationInfo.lng
      return 'https://www.openstreetmap.org/?mlat=' + lat + '&mlon=' + lng + '#map=15/' + lat + '/' + lng
    },

    isInternetSlow () {
      if (!this.isOnline) return true
      if (this.speedTestMbps != null && this.speedTestMbps < 5) return true
      if (this.pingMs != null && this.pingMs > 400) return true
      if (this.speedTestMbps != null && this.speedTestMbps < 15) return true
      if (this.pingMs != null && this.pingMs > 150) return true
      return false
    },

    internetStatus () {
      if (!this.isOnline) return 'red'
      if (this.speedTestLoading) return 'yellow'
      if (this.speedTestError && this.speedTestMbps == null) return 'red'
      var speed = Number(this.speedTestMbps)
      if (Number.isFinite(speed) && speed < 5) return 'red'
      if (Number.isFinite(speed) && speed < 20) return 'yellow'
      if (this.speedTestMbps == null) return 'yellow'
      return 'green'
    },

    latencyStatus () {
      if (!this.isOnline) return 'red'
      if (this.pingMs == null) return 'yellow'
      if (this.pingMs > 400) return 'red'
      if (this.pingMs > 120) return 'yellow'
      return 'green'
    },

    hardwareStatus () {
      var cores = Number(this.hardwareInfo.cores)
      var ram = Number(this.hardwareInfo.deviceMemory)
      if (Number.isFinite(cores) && cores > 0 && cores < 2) return 'red'
      if (Number.isFinite(ram) && ram > 0 && ram < 2) return 'red'
      if (this.memoryInfo.usageHigh && this.memoryInfo.usagePercent !== 'No disponible') {
        var pct = parseInt(this.memoryInfo.usagePercent, 10)
        if (pct >= 90) return 'red'
      }
      if (this.isHardwareLimited) return 'yellow'
      return 'green'
    },

    overallStatus () {
      var statuses = [this.internetStatus, this.latencyStatus, this.hardwareStatus]
      if (statuses.indexOf('red') >= 0) return 'red'
      if (statuses.indexOf('yellow') >= 0) return 'yellow'
      return 'green'
    },

    overallStatusTitle () {
      if (this.overallStatus === 'green') return 'Todo corre al 100%'
      if (this.overallStatus === 'yellow') return 'Rendimiento aceptable'
      return 'Hay problemas detectados'
    },

    overallStatusText () {
      if (this.overallStatus === 'green') {
        return 'Tu conexión y tu equipo están en condiciones óptimas para usar el sistema.'
      }
      if (this.overallStatus === 'yellow') {
        return 'El sistema debería funcionar, pero podrías notar lentitud en algunas tareas.'
      }
      return 'Revisa tu internet o el equipo; el sistema podría fallar o ir muy lento.'
    },

    internetStatusLabel () {
      if (this.internetStatus === 'green') return 'Excelente'
      if (this.internetStatus === 'yellow') return 'Regular'
      return 'Con problemas'
    },

    latencyStatusLabel () {
      if (this.latencyStatus === 'green') return 'Baja'
      if (this.latencyStatus === 'yellow') return 'Media'
      return 'Alta'
    },

    hardwareStatusLabel () {
      if (this.hardwareStatus === 'green') return 'Óptimo'
      if (this.hardwareStatus === 'yellow') return 'Limitado'
      return 'Insuficiente'
    },

    speedStatusClass () {
      if (this.internetStatus === 'green') return 'success--text'
      if (this.internetStatus === 'yellow') return 'warning--text font-weight-bold'
      return 'error--text font-weight-bold'
    },

    latencyStatusClass () {
      if (this.latencyStatus === 'green') return 'success--text'
      if (this.latencyStatus === 'yellow') return 'warning--text font-weight-bold'
      return 'error--text font-weight-bold'
    },

    isHardwareLimited () {
      const cores = Number(this.hardwareInfo.cores)
      const ram = Number(this.hardwareInfo.deviceMemory)
      if (Number.isFinite(cores) && cores > 0 && cores < 4) return true
      if (Number.isFinite(ram) && ram > 0 && ram < 4) return true
      if (this.memoryInfo.usageHigh) return true
      return false
    },
  },

  watch: {
    overallStatus () {
      this.emitStatusUpdate()
    },
  },

  mounted () {
    window.addEventListener('online', this.updateNetworkStatus)
    window.addEventListener('offline', this.updateNetworkStatus)
    window.addEventListener('resize', this.updateScreenInfo)

    this.connectionRef = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (this.connectionRef && this.connectionRef.addEventListener) {
      this.connectionRef.addEventListener('change', this.updateNetworkStatus)
    }

    this.loadDeviceInfo()
    this.hourlyTimer = setInterval(this.guardarDiagnosticoProgramado, DIAG_SEND_INTERVAL_MS)
    this.refreshAll()
  },

  beforeDestroy () {
    if (this.hourlyTimer) {
      clearInterval(this.hourlyTimer)
      this.hourlyTimer = null
    }
    window.removeEventListener('online', this.updateNetworkStatus)
    window.removeEventListener('offline', this.updateNetworkStatus)
    window.removeEventListener('resize', this.updateScreenInfo)
    if (this.connectionRef && this.connectionRef.removeEventListener) {
      this.connectionRef.removeEventListener('change', this.updateNetworkStatus)
    }
  },

  methods: {
    emitStatusUpdate () {
      this.$emit('status-update', {
        overallStatus: this.overallStatus,
        overallStatusTitle: this.overallStatusTitle,
        overallStatusText: this.overallStatusText,
        internetStatusLabel: this.internetStatusLabel,
        latencyStatusLabel: this.latencyStatusLabel,
        hardwareStatusLabel: this.hardwareStatusLabel,
      })
    },

    buildRefItem (key, label, optimo, actual, status) {
      return { key: key, label: label, optimo: optimo, actual: actual, status: status || 'yellow' }
    },

    scoreSpeed () {
      if (this.speedTestLoading || this.speedTestMbps == null) return 'yellow'
      if (this.speedTestMbps >= REFERENCIA_OPTIMA.speedMbps) return 'green'
      if (this.speedTestMbps >= 5) return 'yellow'
      return 'red'
    },

    scorePing () {
      if (this.pingMs == null) return 'yellow'
      if (this.pingMs <= REFERENCIA_OPTIMA.pingMs) return 'green'
      if (this.pingMs <= 400) return 'yellow'
      return 'red'
    },

    scoreCores () {
      var cores = Number(this.hardwareInfo.cores)
      if (!Number.isFinite(cores) || cores <= 0) return 'yellow'
      if (cores >= REFERENCIA_OPTIMA.cores) return 'green'
      if (cores >= 2) return 'yellow'
      return 'red'
    },

    scoreRam () {
      var ram = Number(this.hardwareInfo.deviceMemory)
      if (!Number.isFinite(ram) || ram <= 0) return 'yellow'
      if (ram >= REFERENCIA_OPTIMA.ramGb) return 'green'
      if (ram >= 2) return 'yellow'
      return 'red'
    },

    scoreJsMemory (pct) {
      if (!Number.isFinite(pct)) return 'yellow'
      if (pct < REFERENCIA_OPTIMA.memoryJsPercent) return 'green'
      if (pct < 90) return 'yellow'
      return 'red'
    },

    scoreStorage (availableMb) {
      if (!Number.isFinite(availableMb)) return 'yellow'
      if (availableMb >= REFERENCIA_OPTIMA.storageAvailableMb) return 'green'
      if (availableMb >= 100) return 'yellow'
      return 'red'
    },

    refreshAll (forzarEnvio) {
      this.midiendo = true
      this.updateNetworkStatus()
      this.updateHardwareInfo()
      this.updateScreenInfo()
      this.updateBrowserInfo()
      this.updateMemoryInfo()
      this.updateStorageInfo()
      var pingPromise = this.measurePing()
      var speedPromise = this.measureDownloadSpeed()
      var locationPromise = this.updateLocation()
      Promise.all([pingPromise, speedPromise, locationPromise]).finally(() => {
        this.lastUpdate = new Date().toLocaleString('es-MX')
        this.midiendo = false
        this.emitStatusUpdate()
        this.guardarDiagnostico(!!forzarEnvio)
      })
    },

    guardarDiagnosticoProgramado () {
      this.updateNetworkStatus()
      this.updateMemoryInfo()
      this.updateStorageInfo()
      this.measurePing().finally(() => {
        this.guardarDiagnostico(false)
      })
    },

    getOrCreateDeviceId () {
      try {
        var stored = localStorage.getItem(DIAG_DEVICE_ID_KEY)
        if (stored) return stored
        var generated = 'web-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
        localStorage.setItem(DIAG_DEVICE_ID_KEY, generated)
        return generated
      } catch (e) {
        return 'unknown-device'
      }
    },

    loadDeviceInfo () {
      var self = this
      var fallbackId = this.getOrCreateDeviceId()
      this.deviceInfo.id = fallbackId
      this.deviceInfo.platform = Capacitor.getPlatform()
      this.deviceInfo.platformLabel = Capacitor.getPlatform()

      Device.getId()
        .then(function (result) {
          if (result && result.identifier) {
            self.deviceInfo.id = result.identifier
            try { localStorage.setItem(DIAG_DEVICE_ID_KEY, result.identifier) } catch (e) {}
          }
        })
        .catch(function () {})

      Device.getInfo()
        .then(function (info) {
          if (!info) return
          self.deviceInfo.platform = info.platform || self.deviceInfo.platform
          self.deviceInfo.platformLabel = [info.manufacturer, info.model, info.platform].filter(Boolean).join(' · ') || info.platform
          self.deviceInfo.model = info.model || ''
          self.deviceInfo.osVersion = info.osVersion || ''
          self.deviceInfo.manufacturer = info.manufacturer || ''
          self.deviceInfo.isVirtual = !!info.isVirtual
        })
        .catch(function () {})
    },

    buildDiagnosticoPayload () {
      return {
        capturedAt: new Date().toISOString(),
        usuario: this.panelUsuario,
        device: this.deviceInfo,
        semaforo: {
          general: this.overallStatus,
          internet: this.internetStatus,
          latencia: this.latencyStatus,
          hardware: this.hardwareStatus,
        },
        red: {
          online: this.isOnline,
          type: this.networkInfo.type,
          speedMbps: this.speedTestMbps,
          speedDetail: this.speedTestDetail,
          browserDownlink: this.networkInfo.downlink,
          rtt: this.networkInfo.rtt,
          pingMs: this.pingMs,
          saveData: this.networkInfo.saveData,
        },
        hardware: this.hardwareInfo,
        pantalla: this.screenInfo,
        navegador: this.browserInfo,
        memoriaJs: this.memoryInfo,
        almacenamiento: {
          quota: this.storageInfo.quota,
          usage: this.storageInfo.usage,
          available: this.storageInfo.available,
          usagePercent: this.storageInfo.usageText,
        },
        ubicacion: this.locationInfo,
        referenciaOptima: REFERENCIA_OPTIMA,
        referenciaComparativa: this.referenciaComparativa,
        userAgent: this.browserInfo.userAgent,
      }
    },

    guardarDiagnostico (forzarEnvio) {
      var payload = this.buildDiagnosticoPayload()
      var ahora = new Date().toLocaleString('es-MX')
      this.registroInfo.ultimoGuardado = ahora

      try {
        var historial = JSON.parse(localStorage.getItem(DIAG_LOCAL_KEY) || '[]')
        historial.unshift(payload)
        if (historial.length > 100) historial = historial.slice(0, 100)
        localStorage.setItem(DIAG_LOCAL_KEY, JSON.stringify(historial))
      } catch (e) {}

      var lastSend = 0
      try {
        lastSend = parseInt(localStorage.getItem(DIAG_LAST_SEND_KEY) || '0', 10)
      } catch (e2) {
        lastSend = 0
      }

      if (!forzarEnvio && Date.now() - lastSend < DIAG_SEND_INTERVAL_MS) {
        this.registroInfo.estadoTexto = 'Guardado local (envío al servidor en ≤ 1 h)'
        this.registroInfo.error = ''
        return Promise.resolve()
      }

      this.registroInfo.enviando = true
      this.registroInfo.estadoTexto = 'Enviando al servidor...'

      return axios.post('diagnostico-sistema.data', payload)
        .then(() => {
          this.registroInfo.ultimoEnvioServidor = ahora
          this.registroInfo.estadoTexto = 'Guardado local y en servidor'
          this.registroInfo.error = ''
          try { localStorage.setItem(DIAG_LAST_SEND_KEY, String(Date.now())) } catch (e3) {}
        })
        .catch(() => {
          this.registroInfo.estadoTexto = 'Guardado local (servidor no disponible)'
          this.registroInfo.error = 'offline'
        })
        .finally(() => {
          this.registroInfo.enviando = false
        })
    },

    updateStorageInfo () {
      if (!navigator.storage || !navigator.storage.estimate) {
        this.storageInfo = {
          quota: null,
          usage: null,
          available: null,
          availableText: 'No disponible en este navegador',
          usageText: 'No disponible',
          usageHigh: false,
        }
        return Promise.resolve()
      }

      var self = this
      return navigator.storage.estimate()
        .then(function (estimate) {
          var quota = estimate.quota || 0
          var usage = estimate.usage || 0
          var available = quota > usage ? quota - usage : 0
          var pct = quota > 0 ? Math.round((usage / quota) * 100) : 0
          self.storageInfo = {
            quota: quota,
            usage: usage,
            available: available,
            availableText: self.formatBytes(available) + ' libres',
            usageText: pct + '% usado (' + self.formatBytes(usage) + ' de ' + self.formatBytes(quota) + ')',
            usageHigh: pct >= 85,
          }
        })
        .catch(function () {
          self.storageInfo.availableText = 'No disponible'
        })
    },

    updateNetworkStatus () {
      this.isOnline = navigator.onLine
      const conn = this.connectionRef
      if (conn) {
        this.networkInfo = {
          downlink: conn.downlink != null ? conn.downlink : null,
          rtt: conn.rtt != null ? conn.rtt : null,
          type: conn.effectiveType || conn.type || 'desconocido',
          saveData: !!conn.saveData,
        }
      } else {
        this.networkInfo = {
          downlink: null,
          rtt: null,
          type: 'API no soportada en este navegador',
          saveData: false,
        }
      }
    },

    updateHardwareInfo () {
      this.hardwareInfo = {
        cores: navigator.hardwareConcurrency || null,
        deviceMemory: navigator.deviceMemory || null,
        platform: navigator.platform || null,
        maxTouchPoints: navigator.maxTouchPoints != null ? navigator.maxTouchPoints : null,
      }
    },

    updateScreenInfo () {
      this.screenInfo = {
        width: window.screen.width,
        height: window.screen.height,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        colorDepth: window.screen.colorDepth,
        pixelRatio: window.devicePixelRatio || 1,
      }
    },

    updateBrowserInfo () {
      const ua = navigator.userAgent || ''
      this.browserInfo = {
        userAgent: ua,
        name: this.detectBrowser(ua),
        language: navigator.language || '—',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '—',
      }
    },

    updateMemoryInfo () {
      const perf = performance
      if (perf && perf.memory) {
        const used = perf.memory.usedJSHeapSize
        const limit = perf.memory.jsHeapSizeLimit
        const pct = limit > 0 ? Math.round((used / limit) * 100) : 0
        this.memoryInfo = {
          used: this.formatBytes(used),
          limit: this.formatBytes(limit),
          usagePercent: pct + '%',
          usageHigh: pct >= 80,
        }
      } else {
        this.memoryInfo = {
          used: 'No disponible',
          limit: 'No disponible',
          usagePercent: 'No disponible',
          usageHigh: false,
        }
      }
    },

    measurePing () {
      const url = window.location.origin + '/favicon.ico?t=' + Date.now()
      const start = performance.now()
      return fetch(url, { method: 'HEAD', cache: 'no-store', mode: 'same-origin' })
        .then(() => {
          this.pingMs = Math.round(performance.now() - start)
        })
        .catch(() => {
          this.pingMs = null
        })
    },

    measureDownloadSpeed () {
      this.speedTestLoading = true
      this.speedTestError = false
      this.speedTestMbps = null
      this.speedTestDetail = ''
      this.speedTestProgress = 'Preparando prueba...'

      var self = this
      var platform = Capacitor.getPlatform()

      return this.runCloudflareSpeedSample(3000000, true)
        .then(function () {
          self.speedTestProgress = 'Descarga 1 de 3...'
          return self.runAdaptiveSpeedSample()
        })
        .then(function (sample1) {
          self.speedTestProgress = 'Descarga 2 de 3...'
          return self.runAdaptiveSpeedSample().then(function (sample2) {
            return [sample1, sample2]
          })
        })
        .then(function (samples) {
          self.speedTestProgress = 'Descarga 3 de 3...'
          return self.runAdaptiveSpeedSample().then(function (sample3) {
            samples.push(sample3)
            return samples
          })
        })
        .then(function (samples) {
          var speeds = samples
            .map(function (sample) { return sample.mbps })
            .filter(function (speed) { return Number.isFinite(speed) && speed > 0 })

          if (!speeds.length) {
            self.speedTestError = true
            return self.measureDownloadSpeedFallback()
          }

          speeds.sort(function (a, b) { return a - b })
          var median = speeds[Math.floor(speeds.length / 2)]
          self.speedTestMbps = Math.round(median * 10) / 10

          var sizeMb = Math.round((samples[0].bytes / 1000000) * 10) / 10
          var platformLabel = platform === 'web' ? 'navegador' : ('app ' + platform)
          self.speedTestDetail = 'Descarga real de ' + sizeMb + ' MB × 3 vía Cloudflare (' + platformLabel + ', mediana)'
        })
        .catch(function () {
          self.speedTestError = true
          return self.measureDownloadSpeedFallback()
        })
        .finally(function () {
          self.speedTestLoading = false
          self.speedTestProgress = ''
        })
    },

    runAdaptiveSpeedSample () {
      var self = this
      var bytes = 10000000

      return this.runCloudflareSpeedSample(bytes, false).then(function (sample) {
        if (sample.elapsedSec >= 1.2 || bytes >= 25000000) {
          return sample
        }
        bytes = 20000000
        return self.runCloudflareSpeedSample(bytes, false)
      })
    },

    runCloudflareSpeedSample (bytes, silent) {
      var url = 'https://speed.cloudflare.com/__down?bytes=' + bytes + '&t=' + Date.now() + '&r=' + Math.random()
      var start = performance.now()

      return fetch(url, { cache: 'no-store', mode: 'cors' })
        .then(function (response) {
          if (!response.ok) {
            throw new Error('speed sample failed')
          }
          return response.blob()
        })
        .then(function (blob) {
          var elapsedSec = (performance.now() - start) / 1000
          var mbps = elapsedSec > 0 ? (blob.size * 8) / elapsedSec / 1000000 : 0
          return {
            bytes: blob.size,
            elapsedSec: elapsedSec,
            mbps: mbps,
          }
        })
        .catch(function (err) {
          if (!silent) {
            throw err
          }
          return { bytes: 0, elapsedSec: 0, mbps: 0 }
        })
    },

    measureDownloadSpeedFallback () {
      var self = this
      this.speedTestProgress = 'Probando servidor local...'

      var url = window.location.origin + '/fondo_whatsapp.jpg?t=' + Date.now()
      var start = performance.now()

      return fetch(url, { cache: 'reload', mode: 'same-origin' })
        .then(function (response) { return response.blob() })
        .then(function (blob) {
          var elapsedSec = (performance.now() - start) / 1000
          if (elapsedSec <= 0 || !blob.size) {
            self.speedTestError = true
            return
          }
          var mbps = (blob.size * 8) / elapsedSec / 1000000
          self.speedTestMbps = Math.round(mbps * 10) / 10
          self.speedTestDetail = 'Respaldo local (poco preciso; puede inflar el resultado)'
          self.speedTestError = false
        })
        .catch(function () {
          self.speedTestError = true
        })
    },

    detectBrowser (ua) {
      if (ua.indexOf('Edg/') > -1) return 'Microsoft Edge'
      if (ua.indexOf('Chrome/') > -1) return 'Google Chrome'
      if (ua.indexOf('Firefox/') > -1) return 'Mozilla Firefox'
      if (ua.indexOf('Safari/') > -1 && ua.indexOf('Chrome') === -1) return 'Safari'
      return 'Otro'
    },

    formatBytes (bytes) {
      if (!bytes || !Number.isFinite(bytes)) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      let i = 0
      let val = bytes
      while (val >= 1024 && i < units.length - 1) {
        val /= 1024
        i += 1
      }
      return val.toFixed(i === 0 ? 0 : 1) + ' ' + units[i]
    },

    formatDownlink (val) {
      if (val == null || val === '') return 'No disponible'
      return val + ' Mbps'
    },

    formatBrowserDownlink (val) {
      if (val == null || val === '') return 'No disponible'
      return val + ' Mbps (estimación del navegador, no tu plan)'
    },

    formatSpeedTest (val) {
      if (this.speedTestLoading) {
        return this.speedTestProgress || 'Midiendo...'
      }
      if (this.speedTestError && val == null) return 'No se pudo medir'
      if (val == null) return 'No disponible'
      return val + ' Mbps'
    },

    formatRtt (val) {
      if (val == null || val === '') return 'No disponible'
      return val + ' ms'
    },

    formatDeviceMemory (val) {
      if (val == null || val === '') return 'No disponible (solo Chrome/Edge)'
      return val + ' GB'
    },

    formatCoords (lat, lng) {
      if (lat == null || lng == null) return 'No disponible'
      return Number(lat).toFixed(5) + ', ' + Number(lng).toFixed(5)
    },

    formatAccuracy (accuracy) {
      if (accuracy == null || accuracy === '') {
        return this.locationInfo.source === 'ip' ? 'Aproximada (IP)' : 'No disponible'
      }
      if (accuracy >= 1000) return Math.round(accuracy / 1000) + ' km'
      return Math.round(accuracy) + ' m'
    },

    setLocationFromCoords (lat, lng, accuracy, source) {
      this.locationInfo.lat = lat
      this.locationInfo.lng = lng
      this.locationInfo.accuracy = accuracy
      this.locationInfo.source = source
      this.locationInfo.error = ''
    },

    reverseGeocode (lat, lng) {
      var url = 'https://nominatim.openstreetmap.org/reverse?format=json&lat=' + lat + '&lon=' + lng + '&accept-language=es'
      return fetch(url)
        .then(function (response) { return response.json() })
        .then((data) => {
          if (!data || !data.address) return
          var addr = data.address
          this.locationInfo.address = data.display_name || ''
          this.locationInfo.city = addr.city || addr.town || addr.village || addr.municipality || ''
          this.locationInfo.region = addr.state || addr.region || ''
          this.locationInfo.country = addr.country || ''
        })
        .catch(function () {})
    },

    fetchLocationByIp () {
      return fetch('https://ipapi.co/json/')
        .then(function (response) { return response.json() })
        .then((data) => {
          if (!data || data.error || data.latitude == null) {
            this.locationInfo.error = 'No se pudo determinar la ubicación.'
            return
          }
          this.setLocationFromCoords(data.latitude, data.longitude, null, 'ip')
          this.locationInfo.address = [data.city, data.region, data.country_name].filter(Boolean).join(', ')
          this.locationInfo.city = data.city || ''
          this.locationInfo.region = data.region || ''
          this.locationInfo.country = data.country_name || ''
        })
        .catch(() => {
          this.locationInfo.error = 'No se pudo determinar la ubicación.'
        })
    },

    updateLocation () {
      this.locationInfo.loading = true
      this.locationInfo.error = ''
      this.locationInfo.permissionDenied = false

      var self = this
      return new Promise(function (resolve) {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
          self.fetchLocationByIp().finally(function () {
            self.locationInfo.loading = false
            resolve()
          })
          return
        }

        navigator.geolocation.getCurrentPosition(
          function (position) {
            var coords = position.coords
            self.setLocationFromCoords(coords.latitude, coords.longitude, coords.accuracy, 'gps')
            self.reverseGeocode(coords.latitude, coords.longitude).finally(function () {
              self.locationInfo.loading = false
              resolve()
            })
          },
          function () {
            self.locationInfo.permissionDenied = true
            self.fetchLocationByIp().finally(function () {
              self.locationInfo.loading = false
              resolve()
            })
          },
          {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 60000,
          }
        )
      })
    },
  },
}
</script>

<style scoped>
.diag-card {
  background: #fff;
  border-color: #e8ecf1 !important;
}

.diag-card__title {
  font-size: 1rem;
  font-weight: 600;
}

.diag-section {
  background: #f8fafc;
  border: 1px solid #eef1f6;
  border-radius: 12px;
  padding: 14px 16px;
  height: 100%;
}

.diag-section__head {
  font-size: 0.82rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.diag-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;
}

.diag-item__label {
  display: block;
  font-size: 0.68rem;
  color: #8892a6;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.diag-item__value {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #1a1f36;
  word-break: break-word;
}

.diag-item__value--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.78rem;
}

.diag-ref-table {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.diag-ref-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 48px;
  gap: 8px;
  align-items: center;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #eef1f6;
  font-size: 0.8rem;
}

.diag-ref-row--head {
  background: transparent;
  border: none;
  font-size: 0.68rem;
  font-weight: 700;
  color: #8892a6;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0 10px 4px;
}

.diag-ref-metric {
  font-weight: 600;
  color: #374151;
}

.diag-ref-opt {
  color: #6b7280;
}

.diag-ref-val {
  font-weight: 700;
}

.diag-ref-val--green {
  color: #16a34a;
}

.diag-ref-val--yellow {
  color: #d97706;
}

.diag-ref-val--red {
  color: #dc2626;
}

.diag-ref-status {
  text-align: center;
}

.diag-item__value--muted {
  color: #6b7280;
  font-weight: 500;
  font-size: 0.78rem;
}

.diag-semaphore {
  border-radius: 14px;
  padding: 16px 18px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}

.diag-semaphore--green {
  border-color: #bbf7d0;
  background: linear-gradient(135deg, #f0fdf4 0%, #f8fafc 100%);
}

.diag-semaphore--yellow {
  border-color: #fde68a;
  background: linear-gradient(135deg, #fffbeb 0%, #f8fafc 100%);
}

.diag-semaphore--red {
  border-color: #fecaca;
  background: linear-gradient(135deg, #fef2f2 0%, #f8fafc 100%);
}

.diag-semaphore__panel {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
}

.diag-semaphore__lights {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  background: #1f2937;
  border-radius: 999px;
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.35);
}

.diag-light {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  opacity: 0.25;
  transition: opacity 0.2s ease, box-shadow 0.2s ease;
}

.diag-light--red {
  background: #ef4444;
}

.diag-light--yellow {
  background: #f59e0b;
}

.diag-light--green {
  background: #22c55e;
}

.diag-light--active {
  opacity: 1;
  box-shadow: 0 0 10px currentColor;
}

.diag-light--red.diag-light--active {
  box-shadow: 0 0 12px #ef4444;
}

.diag-light--yellow.diag-light--active {
  box-shadow: 0 0 12px #f59e0b;
}

.diag-light--green.diag-light--active {
  box-shadow: 0 0 12px #22c55e;
}

.diag-semaphore__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
}

.diag-semaphore__subtitle {
  font-size: 0.82rem;
  color: #6b7280;
  margin-top: 4px;
  max-width: 520px;
}

.diag-semaphore__checks {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
}

.diag-check {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4b5563;
}

.diag-check__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  background: #9ca3af;
}

.diag-check--green .diag-check__dot {
  background: #22c55e;
}

.diag-check--yellow .diag-check__dot {
  background: #f59e0b;
}

.diag-check--red .diag-check__dot {
  background: #ef4444;
}

.diag-useragent {
  font-size: 0.72rem;
  color: #6b7280;
  word-break: break-all;
  line-height: 1.4;
  background: #f3f4f6;
  padding: 10px 12px;
  border-radius: 8px;
}

.diag-item--full {
  grid-column: 1 / -1;
}

.diag-map-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #eef2f7;
}

.diag-map {
  display: block;
  width: 100%;
  height: 220px;
  border: 0;
}

@media (max-width: 600px) {
  .diag-grid {
    grid-template-columns: 1fr;
  }

  .diag-ref-row {
    grid-template-columns: 1fr 1fr;
  }

  .diag-ref-row--head {
    display: none;
  }
}
</style>
